import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import WhatsAppButton from './components/WhatsAppButton';

// Lazy load components for better mobile performance
const HomePage = lazy(() => import('./pages/HomePage'));
const DataEngineeringPage = lazy(() => import('./pages/DataEngineeringPage'));
const AIApplicationsPage = lazy(() => import('./pages/AIApplicationsPage'));
const CloudInfrastructurePage = lazy(() => import('./pages/CloudInfrastructurePage'));
const AIAutomationPage = lazy(() => import('./pages/AIAutomationPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostETL = lazy(() => import('./pages/BlogPostETL'));
const BlogPostDataWarehouse = lazy(() => import('./pages/BlogPostDataWarehouse'));
const BlogPostAIROI = lazy(() => import('./pages/BlogPostAIROI'));
const BlogPostDataEngineeringMumbai = lazy(() => import('./pages/BlogPostDataEngineeringMumbai'));
const BlogPostRBICompliance = lazy(() => import('./pages/BlogPostRBICompliance'));

// Loading component
const LoadingSpinner = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: "#0F0E0D" }}
  >
    <div className="text-center">
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
        style={{ borderColor: "#7B9EC0" }}
      ></div>
      <p
        className="text-xs uppercase tracking-[0.2em]"
        style={{ color: "#6B6860", fontFamily: "ui-monospace, monospace" }}
      >
        NextFold
      </p>
    </div>
  </div>
);

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center p-4"
          style={{ background: "#FAFAF8" }}
        >
          <div className="text-center p-8 max-w-2xl">
            <h1 className="text-2xl font-bold mb-4" style={{ color: "#0F0E0D" }}>Something went wrong</h1>
            <p className="mb-6" style={{ color: "#6B6860" }}>We're experiencing technical difficulties. Please try refreshing the page.</p>
            {this.state.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-mono text-red-800 mb-2">
                  <strong>Error:</strong> {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <details className="text-xs font-mono text-red-700">
                    <summary className="cursor-pointer mb-2">Stack trace</summary>
                    <pre className="whitespace-pre-wrap overflow-auto max-h-64">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 font-semibold transition-all duration-200"
              style={{ background: "#1E3A5F", color: "#FAFAF8", border: "1px solid #2952A3" }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// gtag config only fires once at initial page load — this fires a page_view
// on every client-side route change so React Router navigation is tracked in GA4.
const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <WhatsAppButton />
        <RouteChangeTracker />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/data-engineering" element={<DataEngineeringPage />} />
            <Route path="/ai-applications" element={<AIApplicationsPage />} />
            <Route path="/cloud-infrastructure" element={<CloudInfrastructurePage />} />
            <Route path="/ai-automation" element={<AIAutomationPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/etl-pipeline-best-practices-2025" element={<BlogPostETL />} />
            <Route path="/blog/data-warehouse-vs-data-lake" element={<BlogPostDataWarehouse />} />
            <Route path="/blog/ai-automation-roi-guide" element={<BlogPostAIROI />} />
            <Route path="/blog/data-engineering-consultant-mumbai" element={<BlogPostDataEngineeringMumbai />} />
            <Route path="/blog/rbi-compliance-automation-nbfc" element={<BlogPostRBICompliance />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
